import React, { useState, useMemo, useRef, useEffect, type ReactNode } from "react";
import { useFliptBoolean } from "@flipt-io/flipt-client-react";
import { createDatasetAssistantApi } from "datasets/components/assistant/api/DatasetAssistantApi";
import useDataset from "datasets/hooks/useDataset";
import { DocumentModal } from "~/components/DocumentModal";
import { AssistantCore } from "~/components/assistant/AssistantCore";
import { PopoverButton } from "~/components/assistant/PopoverButton";
import type { PresetPrompt } from "~/components/assistant/PresetPrompts";
import {
  ANALYSIS_AGENT_USAGE_GUIDE_PATH,
  ANALYSIS_AGENT_USAGE_GUIDE_TITLE,
} from "~/components/assistant/constants";
import { Button } from "~/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import useUser from "~/contexts/user";
import { cn } from "~/lib/utils";
import { trackEvent } from "~/utils/monitoring";

const { VITE_ANALYSIS_AGENT_API } = import.meta.env;

export interface DatasetAssistantProps {
  datasetId: string;
  mode?: "popover" | "embedded";
  className?: string;
  initialConversationId?: string | null;
  onConversationChange?: (conversationId: string | null) => void;
  showSidebar?: boolean;
  compactMode?: boolean;
  headerActions?: ReactNode;
}

export function DatasetAssistant({
  datasetId,
  mode = "popover",
  className,
  initialConversationId,
  onConversationChange,
  showSidebar,
  compactMode,
  headerActions,
}: DatasetAssistantProps) {
  // Debug mode via feature flag (enabled for ai-team on manifold stage)
  const user = useUser();
  const debugMode = useFliptBoolean("agent-debug-mode", false, user?.email);

  // Dataset-specific data
  const { data: dataset } = useDataset(datasetId);
  const datasetTitle = dataset?.title ?? datasetId;

  // Convert dataset sample prompts to PresetPrompt[] (if any non-empty prompts exist)
  const customPresetPrompts: PresetPrompt[] | undefined = useMemo(() => {
    const prompts = dataset?.samplePrompts
      ?.filter((p) => p.trim().length > 0)
      .map((p) => ({ title: p, prompt: p }));
    return prompts && prompts.length > 0 ? prompts : undefined;
  }, [dataset?.samplePrompts]);

  // Create API instance (memoized to prevent recreation)
  const api = useMemo(
    () => createDatasetAssistantApi(VITE_ANALYSIS_AGENT_API),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [showUsageGuide, setShowUsageGuide] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isFabVisible, setIsFabVisible] = useState(true);
  const [isBackdropFadingOut, setIsBackdropFadingOut] = useState(false);
  // Refs for session tracking
  const sessionStartTimeRef = useRef<number | null>(null);
  const hasTrackedEmbeddedOpenRef = useRef(false);

  const handleToggleFullscreen = () => {
    if (isFullscreen) {
      // Exiting fullscreen - only fade out backdrop
      setIsBackdropFadingOut(true);
      setIsFullscreen(false);
      setTimeout(() => {
        setIsBackdropFadingOut(false);
      }, 200);

      trackEvent("analysis_agent_fullscreen_toggled", {
        dataset_id: datasetId,
        context: "dataset",
        entering_fullscreen: false,
      });
    } else {
      setIsFullscreen(true);

      trackEvent("analysis_agent_fullscreen_toggled", {
        dataset_id: datasetId,
        context: "dataset",
        entering_fullscreen: true,
      });
    }
  };

  const handleOpen = () => {
    sessionStartTimeRef.current = Date.now();

    trackEvent("analysis_agent_opened", {
      dataset_id: datasetId,
      dataset_title: datasetTitle,
      mode,
      context: "dataset",
    });

    setIsFabVisible(false);
    setIsOpen(true);
  };

  const handleClose = () => {
    const sessionDuration = sessionStartTimeRef.current
      ? (Date.now() - sessionStartTimeRef.current) / 1000
      : 0;

    trackEvent("analysis_agent_closed", {
      dataset_id: datasetId,
      mode,
      context: "dataset",
      session_duration_seconds: Math.round(sessionDuration),
    });

    setIsOpen(false);
    setIsFullscreen(false);
    setIsFabVisible(true);
  };

  // Track embedded mode mount and unmount
  useEffect(() => {
    if (mode === "embedded" && !hasTrackedEmbeddedOpenRef.current) {
      hasTrackedEmbeddedOpenRef.current = true;
      sessionStartTimeRef.current = Date.now();

      trackEvent("analysis_agent_opened", {
        dataset_id: datasetId,
        dataset_title: datasetTitle,
        mode,
        context: "dataset",
      });

      // Track when component unmounts (user navigates away)
      return () => {
        const sessionDuration = sessionStartTimeRef.current
          ? (Date.now() - sessionStartTimeRef.current) / 1000
          : 0;

        trackEvent("analysis_agent_closed", {
          dataset_id: datasetId,
          mode: "embedded",
          context: "dataset",
          session_duration_seconds: Math.round(sessionDuration),
        });
      };
    }
  }, [mode, datasetId, datasetTitle]);

  // Usage guide button for empty state
  const usageGuideContent = (
    <Button
      variant="link"
      size="sm"
      onClick={() => setShowUsageGuide(true)}
      className="mt-1 h-auto p-0 gap-1.5"
    >
      View Usage Guide
    </Button>
  );

  // For embedded mode, render the core component directly
  if (mode === "embedded") {
    return (
      <>
        <AssistantCore
          api={api}
          contextId={datasetId}
          contextTitle={datasetTitle}
          mode={mode}
          className={className}
          debugMode={debugMode}
          showSidebar={showSidebar}
          compactMode={compactMode}
          headerActions={headerActions}
          optionalEmptyStateContent={usageGuideContent}
          enableSkills
          presetPrompts={customPresetPrompts}
          initialConversationId={initialConversationId}
          onConversationChange={onConversationChange}
          externalMcpAccessIds={dataset?.externalMcpAccess}
        />
        <DocumentModal
          open={showUsageGuide}
          onClose={() => setShowUsageGuide(false)}
          title={ANALYSIS_AGENT_USAGE_GUIDE_TITLE}
          markdownPath={ANALYSIS_AGENT_USAGE_GUIDE_PATH}
        />
      </>
    );
  }

  // For popover mode, render the FAB and popover
  if (mode === "popover") {
    return (
      <>
        <DocumentModal
          open={showUsageGuide}
          onClose={() => setShowUsageGuide(false)}
          title={ANALYSIS_AGENT_USAGE_GUIDE_TITLE}
          markdownPath={ANALYSIS_AGENT_USAGE_GUIDE_PATH}
        />
        {/* FAB */}
        {isFabVisible && (
          <div className="fixed bottom-6 right-6 z-10 animate-in fade-in duration-200">
            <Tooltip delayDuration={700}>
              <TooltipTrigger asChild>
                <PopoverButton
                  onClick={handleOpen}
                  className="w-10 h-10 rounded-full bg-primary hover:bg-primary/80 transition-colors duration-200 cursor-pointer"
                >
                  <svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <g clipPath="url(#a)">
                      <path
                        d="m9.575 4.55 2.992 6.583 6.583 2.993-6.583 2.992L9.575 23.7l-2.992-6.583L0 14.126l6.583-2.992L9.575 4.55Zm9.637 5.025-1.508-3.28-3.279-1.507 3.28-1.497L19.211 0l1.497 3.291L24 4.788l-3.291 1.508-1.497 3.28Z"
                        fill="white"
                      />
                    </g>
                    <defs>
                      <clipPath id="a">
                        <path fill="white" d="M0 0h24v24H0z" />
                      </clipPath>
                    </defs>
                  </svg>
                </PopoverButton>
              </TooltipTrigger>
              <TooltipContent>
                <p>Dataset Assistant</p>
              </TooltipContent>
            </Tooltip>
          </div>
        )}

        {/* Assistant */}
        {isOpen && (
          <>
            {(isFullscreen || isBackdropFadingOut) && (
              <div
                className={cn(
                  "fixed inset-0 bg-black/70 z-40 cursor-pointer transition-opacity duration-300 ease-out",
                  !isBackdropFadingOut ? "animate-in fade-in" : "animate-out fade-out"
                )}
                onClick={handleClose}
              />
            )}
            <div
              className={cn(
                "fixed z-50 bottom-6 right-6 transition-all duration-300 ease-out",
                isFullscreen
                  ? "w-[calc(100vw-48px)] h-[calc(100vh-48px)] max-h-[calc(100vh-48px)]"
                  : "w-[calc(100vw-48px)] md:w-[60vw] lg:w-[50vw] h-[calc(100vh-100px)] max-h-[calc(100vh-100px)]"
              )}
            >
              <div className="w-full h-full bg-white rounded-xl p-0 overflow-hidden border border-gray-200 shadow-[0px_1px_12px_0px_rgba(0,0,0,0.09)]">
                <AssistantCore
                  api={api}
                  contextId={datasetId}
                  contextTitle={datasetTitle}
                  mode={mode}
                  onToggleFullscreen={handleToggleFullscreen}
                  onClose={handleClose}
                  isFullscreen={isFullscreen}
                  debugMode={debugMode}
                  optionalEmptyStateContent={usageGuideContent}
                  enableSkills
                  presetPrompts={customPresetPrompts}
                  externalMcpAccessIds={dataset?.externalMcpAccess}
                />
              </div>
            </div>
          </>
        )}
      </>
    );
  }

  return null;
}
